import { datadogRum } from '@datadog/browser-rum';
import React, { useContext, useEffect, useState } from 'react';
import { containersApi } from '../api/api';
import { Box, LinearProgress, Link, Stack, Typography } from '@mui/material';
import { joinString } from '../shared/utils';
import { colors } from '../components/shared/ui-extension';
import { groups } from '../constants/p21/role';
import { WarehouseApi } from '../api/warehouse';
import { isToday } from 'date-fns';
export const P21UserContext = React.createContext();

// AI-REVIEW: On every review of this file, verify the JSDoc typedefs below
// (P21User, P21UserPermissions, WarehouseLocation, P21UserContextValue) still
// match useP21UserContext's actual return shape and P21UserProvider's
// underlying state/values. Flag any drift (renamed/added/removed fields,
// changed types) instead of silently updating or ignoring it.

/**
 * @typedef {{
 *   id: string,
 *   role: string,
 *   first_name: string,
 *   last_name: string,
 *   email_address: string,
 *   contact_role_desc?: string,
 *   is_alpha_tester?: boolean,
 *   is_salesrep?: boolean,
 *   is_sales_miner?: boolean,
 * } & Record<string, any>} P21User
 */

/**
 * @typedef {Object} P21UserPermissions
 * @property {boolean} hasPermissionToSeeCost
 * @property {boolean} isSupplyChain
 * @property {boolean} isSalesManager
 * @property {boolean} isConsultant
 * @property {boolean} isSalesMiner
 * @property {boolean} isWarehouse
 * @property {boolean} hasPermissionToChangeRepInOrders
 * @property {boolean} hasPermissionToViewAccountTracker
 */

/**
 * @typedef {{
 *   location_id: string,
 *   location_name: string,
 * } & Record<string, any>} WarehouseLocation
 */

/**
 * @typedef {Object} P21UserContextValue
 * @property {P21User} p21User - The current (possibly logged-in-as-debug) P21 user. Empty object until loaded — check `p21User.id` to know when it's ready.
 * @property {P21User[]} p21Users - All P21 users available for the `debug-p21-user-id` override.
 * @property {(user: P21User) => void} setP21User
 * @property {(group: string[]) => boolean} isP21UserInGroup - Whether the current p21User's role is in the given group array (see `constants/p21/role.js`).
 * @property {boolean} isAlphaTester
 * @property {P21UserPermissions} permissions
 * @property {WarehouseLocation[]} warehouseLocations
 */

/**
 * @returns {P21UserContextValue}
 */
export function useP21UserContext() {
  return useContext(P21UserContext);
}

export const P21UserProvider = ({ children, graphData }) => {
  const [p21User, setP21User] = useState({});
  const [p21Users, setP21Users] = useState([]);
  const [shouldRedirectUser, setShouldRedirectUser] = useState(null);
  const [warehouseLocations, setWarehouseLocations] = useState([]);
  useEffect(() => {
    getUserDetails();
    getWarehouseLocations();
  }, []);

  const getWarehouseLocations = async () => {
    try {
      const { data: warehouseLocationsData } =
        await WarehouseApi.getWarehouseLocations();
      setWarehouseLocations(warehouseLocationsData);
    } catch (e) {
      console.error('Error fetching warehouse locations', e);
    }
  };

  const getCurrentEnvironment = () => {
    const environment = process.env.NEXT_PUBLIC_DATA_DOG_ENV || 'LOCAL';
    if (!environment.includes('PROD')) {
      return environment;
    }
  };

  const currentEnvironment = getCurrentEnvironment();

  const inProd = !currentEnvironment;
  const inAlpha = currentEnvironment == 'ALPHA';

  useEffect(() => {
    if (!p21User?.id) return;

    if (inAlpha && !p21User.is_alpha_tester) {
      const fullPath = window.location.pathname + window.location.search;
      const redirectUrl = `https://pam.containers.com${fullPath}`;
      window.location = redirectUrl;
      return;
    } else if (
      shouldRedirectUser === true &&
      inProd &&
      p21User.is_alpha_tester
    ) {
      localStorage.setItem(
        'lastRedirectDeferDate',
        shouldRedirectUser ? null : new Date().toISOString()
      );

      const redirect = setTimeout(() => {
        if (shouldRedirectUser) {
          const fullPath = window.location.pathname + window.location.search;
          const redirectUrl = `https://pam-alpha.containers.com${fullPath}`;
          window.location = redirectUrl;
        }
      }, 2500);
      return () => clearTimeout(redirect);
    }
  }, [p21User, shouldRedirectUser]);

  const debugUserId = localStorage.getItem('debug-p21-user-id');

  const getUserDetails = async () => {
    try {
      const { data } = await containersApi.get('users/details');
      const { user, users } = data;

      setP21User(
        debugUserId ? users.find((u) => u.id === debugUserId) || user : user
      );
      setP21Users(users);

      const lastRedirectDeferDate = localStorage.getItem(
        'lastRedirectDeferDate'
      );
      const lastRedirectIsToday = () => {
        if (!lastRedirectDeferDate) return false;
        return isToday(new Date(lastRedirectDeferDate));
      };
      setShouldRedirectUser(!lastRedirectIsToday());

      datadogRum.setUser({
        ...user,
        id: user.id, // A unique identifier for the user
        name: joinString([user.first_name, user.last_name], ' '), // The user's name
        email: user.email_address, // The user's email
      });
    } catch (e) {
      console.error(e);
    }
  };

  const isP21UserInGroup = (group) => {
    return group.includes(p21User?.role);
  };

  const isAlphaTester = p21User?.is_alpha_tester;

  const noAlpha = new URLSearchParams(window.location.search).get('no_alpha');
  if (noAlpha == 'true' && shouldRedirectUser) {
    setShouldRedirectUser(false);
  }

  let queryParams = '?no_alpha=true';
  if (window.location.search.length > 0) {
    queryParams += `&${window.location.search.substring(1)}`;
  }
  const redirectLink = `https://pam.containers.com/${window.location.pathname}${queryParams}`;

  const renderBanner = () => {
    if (currentEnvironment) {
      return (
        <Stack
          direction={'row'}
          justifyContent={'center'}
          useFlexGap
          gap={2}
          sx={{
            backgroundColor: colors.purple,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ color: 'white', fontSize: '12px' }}>
            <b>{currentEnvironment}</b> Testing -{' '}
            {graphData?.userPrincipalName || ''}
            {debugUserId ? (
              <span style={{ color: 'yellow' }}>
                {' '}
                (Debugging as user{' '}
                <strong>
                  {joinString([
                    debugUserId,
                    p21User?.role,
                    p21User?.contact_role_desc,
                  ])}
                </strong>
                )
              </span>
            ) : (
              ''
            )}
          </Typography>
          <Typography sx={{ color: 'white', fontSize: '12px' }}>
            <Link sx={{ color: 'white' }} href={redirectLink}>
              Back to Production
            </Link>
          </Typography>
        </Stack>
      );
    }

    return inAlpha ? (
      <Box
        sx={{
          backgroundColor: '#e57200',
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: '12px' }}>
          <Link
            onClick={() => {
              setShouldRedirectUser((prev) => !prev);
            }}
            sx={{ color: 'white' }}
          >
            {shouldRedirectUser
              ? 'Redirecting to PAM Alpha Testing... (Click here to stop)'
              : 'Click to try out the latest features'}
          </Link>
        </Typography>
        {shouldRedirectUser ? <LinearProgress color='warning' /> : false}
      </Box>
    ) : null;
  };

  const permissions = {
    hasPermissionToSeeCost: isP21UserInGroup(groups.unitCostVisible),
    isSupplyChain: isP21UserInGroup(groups.supplyChainVisible),
    isSalesManager: isP21UserInGroup(groups.salesManager),
    isConsultant: p21User?.is_salesrep,
    isSalesMiner: p21User?.is_sales_miner,
    isWarehouse: isP21UserInGroup(groups.warehouseRoles),
    hasPermissionToChangeRepInOrders: isP21UserInGroup(
      groups.authorizedToChangeRepInOrders
    ),
    hasPermissionToViewAccountTracker: isP21UserInGroup(
      groups.salesManagerNoSalesOps
    ),
  };

  return (
    <P21UserContext.Provider
      value={{
        p21User,
        p21Users,
        setP21User,
        isP21UserInGroup,
        isAlphaTester,
        permissions,
        warehouseLocations,
      }}
    >
      {renderBanner()}
      {children}
    </P21UserContext.Provider>
  );
};
